/**
 * reactState — the viewer's reaction taps, pending and failed PER SWIM (build 15).
 *
 * Build 13 kept one global ticket and one global "failed" flag (ClubTab), which was fine while a
 * single card showed one swim's picker. The day roster shows every swim's reaction row at once,
 * so a tap on swim A followed by a tap on swim B handed A's outcome to B's ticket (A's failure
 * never rolled back) and a rollback restored the whole list captured at A's tap (erasing B's).
 * Here each swim owns its ticket, its pending flag, its failure, and the reaction to restore.
 *
 * Pure: the tab holds a `ReactModel` in `$state` and replaces it with each result.
 */
import type { ClubReaction, PresetEmoji } from "./snapshot";
import { upsertReaction } from "./strip";

export interface ReactStatus {
  /** The newest tap on this swim; only its failure rolls the row back. */
  ticket: number;
  memberId: string;
  pending: boolean;
  failed: boolean;
  /** The viewer's last CONFIRMED reaction on this swim, restored if the newest tap fails. */
  prior: ClubReaction | null;
  /** The newest ticket native confirmed (0 = none yet); an older confirmation never overwrites it. */
  confirmed?: number;
  /** What each unsettled tap asked for, by ticket, so a late success can become the baseline. */
  sent?: Record<number, ClubReaction | null>;
}

export interface ReactModel {
  reactions: ClubReaction[];
  status: Record<string, ReactStatus>;
  lastTicket: number;
}

export function reactModel(reactions: readonly ClubReaction[]): ReactModel {
  return { reactions: [...reactions], status: {}, lastTicket: 0 };
}

/** Apply a tap optimistically and mark that swim pending. */
export function reactTap(
  model: ReactModel,
  swimId: string,
  memberId: string,
  emoji: PresetEmoji | null,
  createdAt: string,
): { model: ReactModel; ticket: number } {
  const ticket = model.lastTicket + 1;
  const cur = model.status[swimId];
  // A tap on top of one still in flight keeps the confirmed reaction from before both.
  const prior = cur?.pending ? cur.prior : (model.reactions.find((r) => r.swimId === swimId && r.memberId === memberId) ?? null);
  const asked = emoji === null ? null : { swimId, memberId, emoji, createdAt };
  const sent = { ...(cur?.pending ? cur.sent : {}), [ticket]: asked };
  return {
    ticket,
    model: {
      reactions: upsertReaction(model.reactions, swimId, memberId, emoji, createdAt),
      status: { ...model.status, [swimId]: { ticket, memberId, pending: true, failed: false, prior, confirmed: cur?.pending ? cur.confirmed : 0, sent } },
      lastTicket: ticket,
    },
  };
}

const apply = (reactions: readonly ClubReaction[], swimId: string, memberId: string, r: ClubReaction | null): ClubReaction[] =>
  upsertReaction(reactions, swimId, memberId, r?.emoji ?? null, r?.createdAt ?? "");

/** The bridge answered for `ticket` on `swimId`. A failure rolls back THAT swim only, to the newest
 *  reaction native CONFIRMED (Codex P2: a saved first tap under a failed second one stays). */
export function reactSettled(model: ReactModel, swimId: string, ticket: number, ok: boolean): ReactModel {
  const cur = model.status[swimId];
  if (!cur || !cur.sent || !(ticket in cur.sent)) return model;
  const sent = { ...cur.sent };
  const asked = sent[ticket];
  delete sent[ticket];
  const latest = ticket === cur.ticket;
  if (ok) {
    const newer = ticket > (cur.confirmed ?? 0);
    const prior = newer ? asked : cur.prior;
    const confirmed = newer ? ticket : cur.confirmed;
    // The newest tap already failed and rolled back: this older save is what the club holds now.
    const reactions = !latest && !cur.pending && newer ? apply(model.reactions, swimId, cur.memberId, prior) : model.reactions;
    const status = latest ? { ...cur, pending: false, failed: false, prior, confirmed, sent } : { ...cur, prior, confirmed, sent };
    return { ...model, reactions, status: { ...model.status, [swimId]: status } };
  }
  if (!latest) return { ...model, status: { ...model.status, [swimId]: { ...cur, sent } } };
  const reactions = apply(model.reactions, swimId, cur.memberId, cur.prior);
  return { ...model, reactions, status: { ...model.status, [swimId]: { ...cur, pending: false, failed: true, sent } } };
}

/** A fresh snapshot replaces the list (native echoes confirmed taps back) and clears failures;
 *  a tap still in flight keeps its pending state and its ticket. */
export function reactSnapshot(model: ReactModel, reactions: readonly ClubReaction[]): ReactModel {
  const status = Object.fromEntries(Object.entries(model.status).filter(([, s]) => s.pending));
  return { ...model, reactions: [...reactions], status };
}
